import classNames from "classnames";
import React, { FC, useEffect, useMemo, useState } from "react";
import {
  LuArrowDown,
  LuArrowUp,
  LuChevronLeft,
  LuFolderPlus,
  LuPlus,
  LuTrash2,
} from "react-icons/lu";
import { Link } from "react-router-dom";

import { LottieLoading } from "../../components/graphics/LottieLoading";
import { getLinkedFirm } from "../../sessionStorage/sessionStorage";
import {
  useApplyFolderTemplateMutation,
  useCanManageFolderTemplatesQuery,
  useDeleteFolderTemplateMutation,
  useGetFolderTemplatesQuery,
  useSaveFolderTemplateMutation,
} from "../../slices/DocumentsSlice";
import {
  FolderTemplate,
  FolderVisibility,
  TemplateFolder,
} from "./files.types";
import {
  addTemplateFolder,
  countTemplateFolders,
  expandYear,
  moveTemplateFolder,
  removeTemplateFolder,
  TemplatePath,
  templateProblem,
  THIS_YEAR,
  updateTemplateFolder,
} from "./files.utils";
import {
  DANGER_BUTTON,
  ErrorText,
  errorText,
  FilesModal,
  FolderGlyph,
  GHOST_BUTTON,
  INPUT,
  LABEL,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
  SELECT,
  Toggle,
} from "./ui/FilesUi";

type Draft = Omit<FolderTemplate, "id"> & { id?: string };

const EMPTY: Draft = {
  name: "",
  isDefault: false,
  addYearlyFolders: true,
  folders: [{ name: THIS_YEAR, visibility: "private", children: [] }],
};

const VISIBILITY_CHOICES: {
  value: FolderVisibility;
  label: string;
  top: boolean;
}[] = [
  { value: "inherit", label: "Same as above", top: false },
  { value: "private", label: "Firm only", top: true },
  { value: "shared", label: "Client can see", top: true },
  { value: "uploads", label: "Client can see and add", top: true },
];

/** Settings > Templates > Folder templates. */
export const FolderTemplatesSettings: FC = () => {
  const orgId = getLinkedFirm()?.orgId || "";
  const { data: templates, isLoading } = useGetFolderTemplatesQuery(orgId, {
    skip: !orgId,
  });
  const { data: canManage } = useCanManageFolderTemplatesQuery(orgId, {
    skip: !orgId,
  });
  const [save, { isLoading: saving }] = useSaveFolderTemplateMutation();
  const [remove, { isLoading: removing }] = useDeleteFolderTemplateMutation();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [applying, setApplying] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const year = new Date().getFullYear();

  useEffect(() => {
    if (!templates || draft) return;
    const first = templates.find((t) => t.isDefault) || templates[0];
    if (first) {
      setSelectedId(first.id);
      setDraft(first);
    }
  }, [templates]);

  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(""), 4000);
    return () => clearTimeout(timer);
  }, [notice]);

  const pick = (template: FolderTemplate | null) => {
    setError("");
    setDirty(false);
    setSelectedId(template?.id || null);
    setDraft(
      template
        ? template
        : { ...EMPTY, folders: EMPTY.folders.map((f) => ({ ...f })) },
    );
  };

  const change = (next: Partial<Draft>) => {
    setDraft((d) => (d ? { ...d, ...next } : d));
    setDirty(true);
  };

  const problem = draft ? templateProblem(draft.name, draft.folders) : null;

  const submit = async () => {
    if (!draft) return;
    setError("");
    if (problem) {
      setError(problem);
      return;
    }
    try {
      const saved = await save({
        id: draft.id,
        orgId,
        name: draft.name.trim(),
        isDefault: draft.isDefault,
        addYearlyFolders: draft.addYearlyFolders,
        folders: draft.folders,
      }).unwrap();
      setDraft(saved);
      setSelectedId(saved.id);
      setDirty(false);
      setNotice("Template saved.");
    } catch (e) {
      setError(errorText(e));
    }
  };

  const readOnly = !canManage;

  if (isLoading) return <LottieLoading />;

  return (
    <div className={"mb-[120px] py-6 font-Manrope text-[#1F2433]"}>
      <Link
        to={".."}
        relative={"path"}
        className={
          "mb-3 inline-flex items-center gap-1 text-[13px] font-semibold text-[#667085] hover:text-[#5A3EEB]"
        }>
        <LuChevronLeft size={15} />
        Templates
      </Link>
      <div className={"mb-1 text-2xl font-semibold text-[#253747]"}>
        Folder templates
      </div>
      <div className={"mb-5 max-w-[720px] text-sm text-[#8A99AA]"}>
        The folders a client starts with. The default template is added the
        first time someone opens the client’s files (the client’s Files tab, a
        work item’s Files tab or Documents). Applying a template only adds
        folders that are missing; it never moves or deletes anything. “
        {THIS_YEAR}” becomes the current year ({year}).
      </div>
      {readOnly && (
        <div
          className={
            "mb-4 max-w-[720px] rounded-lg bg-[#FFF2DC] px-3 py-2 text-[13px] font-semibold text-[#A15C00]"
          }>
          You can look at the templates, but only Admins and people with “View
          Settings” can change them.
        </div>
      )}

      <div
        className={
          "grid max-w-[1040px] gap-4 md:grid-cols-[230px_minmax(0,1fr)]"
        }>
        <div className={"rounded-2xl border border-[#E6E9EF] bg-white p-2"}>
          {(templates || []).map((t) => (
            <button
              key={t.id}
              type={"button"}
              onClick={() =>
                dirty && !window.confirm?.("Discard your changes?")
                  ? undefined
                  : pick(t)
              }
              className={classNames(
                "flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-[13px] font-semibold",
                selectedId === t.id
                  ? "bg-[#EFEBFF] text-[#4B32D6]"
                  : "hover:bg-[#F7F8FA]",
              )}>
              <span className={"min-w-0 flex-1 truncate"}>{t.name}</span>
              {t.isDefault && (
                <span
                  className={
                    "rounded-full bg-[#E3F6EE] px-2 text-[11px] font-bold text-[#0E8A5F]"
                  }>
                  Default
                </span>
              )}
            </button>
          ))}
          {!readOnly && (
            <button
              type={"button"}
              className={classNames(GHOST_BUTTON, "mt-1 w-full justify-start")}
              onClick={() => pick(null)}>
              <LuPlus size={14} />
              New template
            </button>
          )}
        </div>

        {draft && (
          <div className={"rounded-2xl border border-[#E6E9EF] bg-white p-5"}>
            <label htmlFor={"template-name"} className={LABEL}>
              Template name
            </label>
            <input
              id={"template-name"}
              className={classNames(INPUT, "max-w-[360px]")}
              disabled={readOnly}
              value={draft.name}
              maxLength={100}
              placeholder={"e.g. Corporation + payroll"}
              onChange={(event) => change({ name: event.target.value })}
            />

            <div className={classNames(LABEL, "mt-5")}>
              Folders{" "}
              <span className={"font-semibold text-[#9AA0B0]"}>
                · {countTemplateFolders(draft.folders)}
              </span>
            </div>
            <div
              className={"rounded-xl border border-[#EDEFF3] bg-[#F8F9FC] p-2"}>
              <TemplateRows
                folders={draft.folders}
                level={draft.folders}
                parent={[]}
                year={year}
                readOnly={readOnly}
                onChange={(folders) => change({ folders })}
              />
              {!readOnly && (
                <button
                  type={"button"}
                  className={classNames(GHOST_BUTTON, "mt-1")}
                  onClick={() =>
                    change({
                      folders: addTemplateFolder(
                        draft.folders,
                        [],
                        "New folder",
                      ),
                    })
                  }>
                  <LuFolderPlus size={15} />
                  Add top-level folder
                </button>
              )}
            </div>
            {!readOnly && (
              <p className={"mt-1.5 text-xs text-[#9AA0B0]"}>
                Tip: type {THIS_YEAR} in a folder name for the year (e.g. “
                {THIS_YEAR}” or “Tax {THIS_YEAR}”).
              </p>
            )}

            <div className={"mt-5 flex flex-col gap-3"}>
              <Toggle
                id={"template-default"}
                checked={draft.isDefault}
                disabled={readOnly}
                label={"Use for every new client"}
                hint={
                  "Clients get these folders the first time their Files tab is opened. Only one template can be the default."
                }
                onChange={(checked) => change({ isDefault: checked })}
              />
              <Toggle
                id={"template-yearly"}
                checked={draft.addYearlyFolders}
                disabled={readOnly}
                label={"Add next year’s folders in January"}
                hint={
                  "In the first week of January the “{this year}” folders are added for the new year."
                }
                onChange={(checked) => change({ addYearlyFolders: checked })}
              />
            </div>

            {!readOnly && (
              <div className={"mt-6 flex flex-wrap items-center gap-2"}>
                <button
                  type={"button"}
                  className={PRIMARY_BUTTON}
                  disabled={saving}
                  onClick={submit}>
                  {saving ? "Saving…" : "Save template"}
                </button>
                {draft.id && (
                  <button
                    type={"button"}
                    className={SECONDARY_BUTTON}
                    disabled={dirty}
                    title={dirty ? "Save first" : undefined}
                    onClick={() => setApplying(true)}>
                    Apply to existing clients…
                  </button>
                )}
                <span className={"flex-1"} />
                {draft.id && (
                  <button
                    type={"button"}
                    className={classNames(
                      GHOST_BUTTON,
                      "text-[#C2353A] hover:bg-[#FDE8E8]",
                    )}
                    onClick={() => setConfirmDelete(true)}>
                    <LuTrash2 size={14} />
                    Delete template
                  </button>
                )}
              </div>
            )}
            {notice && (
              <div
                role={"status"}
                className={
                  "mt-3 rounded-lg bg-[#E3F6EE] px-3 py-2 text-[13px] font-semibold text-[#0E8A5F]"
                }>
                {notice}
              </div>
            )}
            <ErrorText>{error}</ErrorText>
          </div>
        )}
      </div>

      {applying && draft?.id && (
        <ApplyTemplateModal
          template={draft as FolderTemplate}
          onClose={() => setApplying(false)}
          onDone={(message) => {
            setApplying(false);
            setNotice(message);
          }}
        />
      )}
      {confirmDelete && draft?.id && (
        <FilesModal
          title={`Delete “${draft.name}”?`}
          onClose={() => setConfirmDelete(false)}
          busy={removing}
          width={420}
          footer={
            <>
              <button
                type={"button"}
                className={SECONDARY_BUTTON}
                onClick={() => setConfirmDelete(false)}>
                Cancel
              </button>
              <button
                type={"button"}
                className={DANGER_BUTTON}
                disabled={removing}
                onClick={async () => {
                  try {
                    await remove(draft.id as string).unwrap();
                    setConfirmDelete(false);
                    setDraft(null);
                    setSelectedId(null);
                    setNotice("Template deleted.");
                  } catch (e) {
                    setConfirmDelete(false);
                    setError(errorText(e));
                  }
                }}>
                Delete
              </button>
            </>
          }>
          <p className={"text-[13.5px] text-[#4A5168]"}>
            Clients keep the folders they already have. New clients won’t get
            this template anymore.
          </p>
        </FilesModal>
      )}
    </div>
  );
};

/** One level of the template tree (rows with name, who can see, order, delete, add subfolder). */
const TemplateRows: FC<{
  folders: TemplateFolder[];
  level: TemplateFolder[];
  parent: TemplatePath;
  year: number;
  readOnly: boolean;
  onChange: (folders: TemplateFolder[]) => void;
}> = ({ folders, level, parent, year, readOnly, onChange }) => (
  <>
    {level.map((folder, index) => {
      const path = [...parent, index];
      const top = parent.length === 0;
      const showsYear = folder.name.toLowerCase().includes(THIS_YEAR);
      return (
        <div key={path.join("-")}>
          <div
            className={
              "group flex flex-wrap items-center gap-2 rounded-lg py-1 pr-1 hover:bg-white"
            }
            style={{ paddingLeft: 6 + parent.length * 22 }}>
            <FolderGlyph size={17} className={"text-[#6B4EFF]"} />
            <input
              aria-label={"Folder name"}
              className={classNames(INPUT.replace("w-full", ""), "h-8 w-[240px] max-w-full")}
              value={folder.name}
              disabled={readOnly}
              maxLength={100}
              onChange={(event) =>
                onChange(
                  updateTemplateFolder(folders, path, {
                    name: event.target.value,
                  }),
                )
              }
            />
            {showsYear && (
              <span className={"text-xs text-[#9AA0B0]"}>
                → {expandYear(folder.name, year)}
              </span>
            )}
            <select
              aria-label={"Who can see it"}
              className={classNames(SELECT, "h-8 text-xs")}
              disabled={readOnly}
              value={folder.visibility || (top ? "private" : "inherit")}
              onChange={(event) =>
                onChange(
                  updateTemplateFolder(folders, path, {
                    visibility: event.target.value as FolderVisibility,
                  }),
                )
              }>
              {VISIBILITY_CHOICES.filter((c) => !top || c.top).map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
            {!readOnly && (
              <span
                className={
                  "ml-auto flex items-center gap-0.5 opacity-70 group-hover:opacity-100"
                }>
                <IconButton
                  label={"Add subfolder"}
                  onClick={() =>
                    onChange(addTemplateFolder(folders, path, "New folder"))
                  }>
                  <LuFolderPlus size={14} />
                </IconButton>
                <IconButton
                  label={"Move up"}
                  disabled={index === 0}
                  onClick={() =>
                    onChange(moveTemplateFolder(folders, path, -1))
                  }>
                  <LuArrowUp size={14} />
                </IconButton>
                <IconButton
                  label={"Move down"}
                  disabled={index === level.length - 1}
                  onClick={() =>
                    onChange(moveTemplateFolder(folders, path, 1))
                  }>
                  <LuArrowDown size={14} />
                </IconButton>
                <IconButton
                  label={"Remove folder"}
                  danger={true}
                  onClick={() => onChange(removeTemplateFolder(folders, path))}>
                  <LuTrash2 size={14} />
                </IconButton>
              </span>
            )}
          </div>
          {folder.children?.length > 0 && (
            <TemplateRows
              folders={folders}
              level={folder.children}
              parent={path}
              year={year}
              readOnly={readOnly}
              onChange={onChange}
            />
          )}
        </div>
      );
    })}
  </>
);

const IconButton: FC<{
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  children: React.ReactNode;
}> = ({ label, onClick, disabled, danger, children }) => (
  <button
    type={"button"}
    aria-label={label}
    title={label}
    disabled={disabled}
    onClick={onClick}
    className={classNames(
      "flex h-7 w-7 items-center justify-center rounded-md disabled:opacity-30",
      danger
        ? "text-[#C2353A] hover:bg-[#FDE8E8]"
        : "text-[#667085] hover:bg-[#EFEBFF]",
    )}>
    {children}
  </button>
);

/** "Apply to existing clients": every client of the firm (only missing folders are added). */
const ApplyTemplateModal: FC<{
  template: FolderTemplate;
  onClose: () => void;
  onDone: (message: string) => void;
}> = ({ template, onClose, onDone }) => {
  const [apply, { isLoading }] = useApplyFolderTemplateMutation();
  const [error, setError] = useState("");
  const count = useMemo(
    () => countTemplateFolders(template.folders),
    [template],
  );
  return (
    <FilesModal
      title={`Apply “${template.name}” to all clients?`}
      onClose={onClose}
      busy={isLoading}
      width={460}
      footer={
        <>
          <button
            type={"button"}
            className={SECONDARY_BUTTON}
            onClick={onClose}
            disabled={isLoading}>
            Cancel
          </button>
          <button
            type={"button"}
            className={PRIMARY_BUTTON}
            disabled={isLoading}
            onClick={async () => {
              setError("");
              try {
                const result = await apply({
                  id: template.id,
                  allClients: true,
                }).unwrap();
                onDone(
                  `Done: ${result.foldersAdded} folder${
                    result.foldersAdded === 1 ? "" : "s"
                  } added across ${result.clients} client${
                    result.clients === 1 ? "" : "s"
                  }.`,
                );
              } catch (e) {
                setError(errorText(e));
              }
            }}>
            {isLoading
              ? "Applying… (this can take a minute)"
              : "Apply to all clients"}
          </button>
        </>
      }>
      <p className={"text-[13.5px] leading-relaxed text-[#4A5168]"}>
        Each client gets the template’s {count} folder{count === 1 ? "" : "s"}{" "}
        where they don’t have them yet. Folders with the same name are reused.
        Nothing is moved or deleted.
      </p>
      <ErrorText>{error}</ErrorText>
    </FilesModal>
  );
};

export default FolderTemplatesSettings;
